<template>
  <div class="audio-container">
    <audio :src="file" :data-matomo-title="file"></audio>
    <div class="controls-container">
      <div class="play-pause-button">
        <i class="fas fa-play" @click="play()" aria-label="Play/Pause" v-if="paused"></i>
        <font-awesome-icon icon="play" @click="play()" aria-label="Play/Pause" v-if="paused"></font-awesome-icon>
        <font-awesome-icon icon="pause" @click="pause()" aria-label="Play/Pause" v-if="!paused"></font-awesome-icon>
        <i class="fas fa-pause" @click="pause()" aria-label="Play/Pause" v-if="!paused"></i>
      </div>
      <div class="time-display">{{ currentTime }}</div>
      <div class="scrub-bar" v-on:click="setPosition">
        <div class="slider" :aria-valuemax="totalDuration" aria-valuemin="0" aria-label="Time scrub bar" tabindex="0" v-on:click="setPosition"></div>
        <div class="container">
          <div class="scrub-bar-current-time" :style="progressStyle"></div>
        </div>
      </div>
      <div class="time-display">{{ currentTimeLeft }}</div>
      <div class="volume">
        <div class="mute-button">
          <font-awesome-icon icon="volume-mute" @click="mute" v-if="isMuted"></font-awesome-icon>
          <font-awesome-icon icon="volume-up" @click="mute" v-if="!isMuted"></font-awesome-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
let convertTimeHHMMSS = (val) => {
  let hhmmss = new Date(val * 1000).toISOString().substr(11, 8);
  return (hhmmss.indexOf('00:') === 0) ? hhmmss.substr(3) : hhmmss;
};

let generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx'.replace(/[xy]/g, function (c) {
    let v, r;
    r = Math.random() * 16 | 0;
    v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export default {
  name: 'AudioPlayer',
  props: {
    file: {
      type: String,
      default: null,
      required: true
    }
  },
  computed: {
    duration: function () {
      return this.audio ? convertTimeHHMMSS(this.totalDuration) : '';
    }
  },
  data () {
    return {
      isMuted: false,
      loaded: false,
      playing: false,
      paused: true,
      progressStyle: 'width:0%;',
      currentTime: '00:00',
      currentTimeLeft: '00:00',
      uuid: '0',
      innerLoop: undefined,
      audio: undefined,
      totalDuration: 0,
      hideVolumeSlider: false,
      volumeValue: 7.5
    };
  },
  methods: {
    setPosition: function (e) {
      let tag = e.target;
      if (e.target.tagName === 'SPAN') {
        return;
      }
      const pos = tag.getBoundingClientRect();
      const seekPos = (e.clientX - pos.left) / pos.width;
      this.audio.currentTime = parseInt(this.audio.duration * seekPos);
    },
    play: function () {
      if (this.playing && !this.paused) return;
      this.paused = false;
      this.audio.play();
      this.playing = true;
    },
    pause: function () {
      this.paused = !this.paused;
      (this.paused) ? this.audio.pause() : this.audio.play();
    },
    mute: function () {
      this.isMuted = !this.isMuted;
      this.audio.muted = this.isMuted;
      this.volumeValue = this.isMuted ? 0 : 75;
    },
    _handleLoaded: function () {
      if (this.audio.readyState >= 2) {
        this.loaded = true;
        this.totalDuration = parseInt(this.audio.duration);
        this.currentTimeLeft = convertTimeHHMMSS(this.totalDuration);
      } else {
        throw new Error('Failed to load sound file');
      }
    },
    _handlePlayingUI: function () {
      let currTime = parseInt(this.audio.currentTime);
      let percentage = parseInt((currTime / this.totalDuration) * 100);
      this.progressStyle = `width:${percentage}%;`;
      this.currentTime = convertTimeHHMMSS(currTime);
      this.currentTimeLeft = convertTimeHHMMSS(this.totalDuration - currTime);
    },
    _handlePlayPause: function (e) {
      if (e.type === 'pause' && this.playing === false) {
        this.progressStyle = `width:0%;`;
        this.currentTime = '00:00';
        this.paused = true;
      }
    },
    init: function () {
      this.audio.addEventListener('timeupdate', this._handlePlayingUI);
      this.audio.addEventListener('loadeddata', this._handleLoaded);
      this.audio.addEventListener('pause', this._handlePlayPause);
      this.audio.addEventListener('play', this._handlePlayPause);
    },
    getAudio: function () {
      return this.$el.querySelectorAll('audio')[0];
    }
  },
  mounted: function () {
    this.uuid = generateUUID();
    this.audio = this.getAudio();
    this.innerLoop = this.loop;
    this.init();
  },
  beforeDestroy: function () {
    this.audio.removeEventListener('timeupdate', this._handlePlayingUI);
    this.audio.removeEventListener('loadeddata', this._handleLoaded);
    this.audio.removeEventListener('pause', this._handlePlayPause);
    this.audio.removeEventListener('play', this._handlePlayPause);
  },
};
</script>

<style lang="scss">
  .audio-container {
    width: auto;
    max-width: 400px;
    height: 50px;
    position: relative;
    background-color: #000;
    display: block;
    flex-direction: column;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: moz-none;
    -ms-user-select: none;
    user-select: none;

    .controls-container {
      width: 100%;
      height: 50px;
      background-color: #eb5d2a !important;
      position: absolute;
      display: table;
      zoom: 1;
      color: white;

      .play-pause-button {
        display: table-cell;
        width: 50px;
        vertical-align: middle;
        text-align: center;
        cursor: pointer;

        button {
          color: #fff;
          speak: none;
          font-style: normal;
          font-weight: 400;
          font-variant: normal;
          text-transform: none;
          line-height: 1;
          -webkit-font-smoothing: antialiased;
          background: 0 0;
          padding: 6px;
          border: none;
        }
      }

      .time-display {
        color: #fff;
        display: table-cell;
        font-family: Arial;
        font-size: 18px;
        width: 75px;
        vertical-align: middle;
        text-align: center;
        cursor: default;
      }

      .scrub-bar {
        width: auto;
        display: table-cell;
        cursor: pointer;
        vertical-align: middle;
        position: relative;

        .container {
          position: absolute;
          width: 100%;
          height: 2px;
          top: 24px;
          padding-left: 0;
          &::before, &::after {
            content: " ";
            display: table;
          }
        }

        .slider {
          margin-top: 24px;
          margin-bottom: 24px;
          height: 2px;
          background-color: #000;
          position: relative;
        }

        .scrub-bar-current-time {
          position: absolute;
          background-color: #fff;
          width: 100%;
          height: 100%;
          display: block;
          cursor: pointer;
        }
      }
    }

    .volume {
      display: table-cell;
      width: 50px;
      vertical-align: middle;
      text-align: center;
      cursor: pointer;
      .mute-button {
        width: 50px;
        display: block;
      }
    }
  }


</style>